import { getKeysForVisiblityConfig } from '../../utils/commonUtil';

export type TriggerPair = { actionType: string; values: string[] };

export type TriggerSource = {
    actionType?: string;
    dropdownOption?: string;
    triggers?: TriggerPair[];
};

/**
 * Canonical trigger shape. `triggers` wins; otherwise synthesise a single pair
 * from `actionType` + optional `dropdownOption`. Returns [] when actionType is
 * absent (state not yet initialised).
 */
export function toTriggerPairs(data: TriggerSource = {}): TriggerPair[] {
    if (Array.isArray(data.triggers)) return data.triggers;
    if (!data.actionType) return [];
    return [{ actionType: data.actionType, values: data.dropdownOption ? [data.dropdownOption] : [] }];
}

/**
 * Flatten trigger pairs into the key shapes consumed by godzilla maps.
 * - specificKeys: `<prefix.>entity.actionType.value` per (pair, value)
 * - genericKeys:  `<prefix.>entity.actionType` deduped per actionType
 */
export function buildTriggerKeys(
    entity: string,
    pairs: TriggerPair[],
    prefix: string = '',
): { specificKeys: string[]; genericKeys: string[] } {
    const specificKeys: string[] = [];
    const genericSet = new Set<string>();

    pairs.forEach((p) => {
        const { baseKey } = getKeysForVisiblityConfig(prefix, entity, p.actionType);
        if (baseKey) genericSet.add(baseKey);
        p.values.forEach((v) => {
            const { specificKey } = getKeysForVisiblityConfig(prefix, entity, p.actionType, v);
            if (specificKey) specificKeys.push(specificKey);
        });
    });

    return { specificKeys, genericKeys: Array.from(genericSet) };
}

export type VisibilityTier = 'specific' | 'generic' | 'none';

export type EvaluationResult<T> = {
    visible: boolean;
    tier: VisibilityTier;
    matched: T[];
};

/**
 * Decide whether a smart-check modal field is visible, given a config map and
 * the trigger keys derived from the user's current action.
 *
 * ## What is a "family"?
 * A family is one trigger source — e.g. a comment-with-call-status, or a tag.
 * In key form, a family is the `<entity>.<actionType>` prefix (e.g.
 * `Lead.comment` or `Lead.tag`). A specific key like `Lead.comment.PASSED`
 * belongs to family `Lead.comment`; the trailing `.PASSED` segment is the
 * specific value the family was triggered with.
 *
 * ## How precedence works (per family, then unioned)
 * For each family we ask: "did the config author write a specific override
 * for this exact value?"
 *
 *   1. If YES (any specific key for this family is defined on the map),
 *      that family is decided by its specifics alone — the family contributes
 *      if at least one specific entry passes `isVisible`. The family's generic
 *      key is intentionally ignored, so an explicit `Lead.comment.PASSED:false`
 *      hides the field even when `Lead.comment` says visible.
 *
 *   2. If NO (no specific key defined for this family), fall back to the
 *      family's generic key. The family contributes if `isVisible` passes
 *      on the generic entry.
 *
 *   3. The final `matched` array is the **union** of contributions across
 *      every family — so a Lead firing both `comment` and `tag` shows
 *      fields drawn from either source.
 *
 * ## Worked example
 * User saves a Lead with `logCallStatus = PASSED` and `tags = [PITCHED]`.
 * `buildTriggerKeys` produces:
 *   - specificKeys: ['Lead.comment.PASSED', 'Lead.tag.PITCHED']
 *   - genericKeys:  ['Lead.comment', 'Lead.tag']
 *
 * Suppose the column's map looks like:
 *   { 'Lead.comment.PASSED': { isMandatory: true },   // specific override
 *     'Lead.tag': { isMandatory: false } }            // generic for any tag
 *
 * Family `Lead.comment` → has a specific defined → uses it → contributes
 *   `{ isMandatory: true }`.
 * Family `Lead.tag` → no `Lead.tag.PITCHED` defined → falls back to
 *   generic `Lead.tag` → contributes `{ isMandatory: false }`.
 * Result: `{ visible: true, tier: 'specific', matched: [{...:true}, {...:false}] }`.
 *
 * The pre-fix evaluator treated "any specific defined anywhere" as a global
 * override and would have dropped the tag-side contribution entirely.
 *
 * ## Return shape
 *   - `visible`: at least one family contributed an isVisible entry.
 *   - `tier`: how we got there — `'specific'` if any family used specifics
 *     (even when those specifics resolved to hidden), `'generic'` if every
 *     contributing family used generic, `'none'` if nothing contributed.
 *   - `matched`: the entries every caller can reduce over (e.g. the
 *     mandatoriness collision rule) without re-walking the map.
 *
 * Used by:
 *   - `useConfigTableModal.filteredColumns`  — column-level visibility
 *   - `InvestorPrivateRoutes.resolveShouldShow` — route-level "should the
 *     modal even open" gate. Both layers share this function so they
 *     can't drift on precedence.
 */
export function evaluateAgainstMap<T>(
    map: Record<string, T> | undefined | null,
    specificKeys: string[],
    genericKeys: string[],
    isVisible: (v: T) => boolean,
): EvaluationResult<T> {
    // Empty / missing config map → nothing visible. Callers treat this as
    // "the modal column is not configured for this entity yet".
    if (!map || typeof map !== 'object') {
        return { visible: false, tier: 'none', matched: [] };
    }

    // Group specific keys by their family. A specific key like
    // `Lead.comment.PASSED` shares its family prefix `Lead.comment` with the
    // generic key for the same actionType, which is exactly how we route
    // each specific to the right family bucket.
    const specificByFamily = new Map<string, string[]>();
    specificKeys.forEach((k) => {
        const lastDot = k.lastIndexOf('.');
        const family = lastDot === -1 ? k : k.slice(0, lastDot);
        const existing = specificByFamily.get(family);
        if (existing) existing.push(k);
        else specificByFamily.set(family, [k]);
    });

    // Families to evaluate: the union of generic keys (one per actionType
    // the user triggered) and any family that has specific keys. Using a
    // Set dedupes the case where a family appears in both lists.
    const families = new Set<string>([...genericKeys, ...specificByFamily.keys()]);
    const matched: T[] = [];
    let anyFamilyUsedSpecific = false;
    let anyFamilyUsedGeneric = false;

    families.forEach((family) => {
        const familySpecifics = specificByFamily.get(family) || [];
        const definedSpecific = familySpecifics.map((k) => map[k]).filter((v): v is T => v !== undefined);

        // Family path 1 — config author wrote a specific override for this
        // family. Specifics decide alone; the generic key is ignored even if
        // present (this is what lets `Lead.comment.PASSED:false` hide a
        // column the broader `Lead.comment` rule would have shown).
        if (definedSpecific.length > 0) {
            anyFamilyUsedSpecific = true;
            definedSpecific.filter(isVisible).forEach((v) => matched.push(v));
            return;
        }

        // Family path 2 — no specifics for this family, use the generic.
        // Only consult `genericKeys` here so we don't accidentally treat a
        // family that exists only via specifics-from-another-actionType as
        // having a generic fallback.
        if (genericKeys.includes(family)) {
            const v = map[family];
            if (v !== undefined) {
                anyFamilyUsedGeneric = true;
                if (isVisible(v)) matched.push(v);
            }
        }
    });

    // `tier` records HOW we resolved, not just the result, so callers can
    // trace decisions in logs. A family using specifics counts as 'specific'
    // even when those specifics evaluated hidden — the override mechanism
    // is what mattered, not whether anything was visible at the end.
    let tier: VisibilityTier = 'none';
    if (anyFamilyUsedSpecific) tier = 'specific';
    else if (anyFamilyUsedGeneric) tier = 'generic';

    return { visible: matched.length > 0, tier, matched };
}

/**
 * Truthy-or-object visibility check shared by route gate and column filter.
 * - boolean → use the value directly
 * - object  → present means show
 * - else    → hide
 */
export function shouldShowField(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'object' && value !== null) return true;
    return false;
}

/**
 * Mandatoriness collision rule: a field is mandatory only when every matched
 * visible entry demands it. Any optional entry wins. Hidden entries are
 * already excluded by `evaluateAgainstMap` (they don't pass `isVisible`).
 */
export function resolveIsMandatory(matched: unknown[]): boolean {
    if (matched.length === 0) return true;
    return matched.every((v) => {
        if (typeof v === 'object' && v !== null && 'isMandatory' in (v as Record<string, unknown>)) {
            return (v as { isMandatory?: unknown }).isMandatory !== false;
        }
        return true;
    });
}
